import { useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CropIcon from '@mui/icons-material/Crop';
import UploadIcon from '@mui/icons-material/Upload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PublishIcon from '@mui/icons-material/Publish';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { IdentityPhotoCropDialog } from '@/features/admin/components/IdentityPhotoCropDialog';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { isCroppableImage, previewCardPhotoUrl } from '@/features/admin/utils/identityCardPhoto';
import { GuideIdentityCard } from '@/features/guide-identity/components/GuideIdentityCard';
import { GuideCardActions } from '@/features/guide-identity/components/GuideCardActions';
import { toGuideCardData } from '@/features/guide-identity/utils/guideCardData';
import { dashboardSectionPanelSx } from '@/shared/styles/surfaces';
import type {
  AdminIdentityCard,
  IdentityCardContent,
  IdentityCardCrop,
  IdentityCardDraftInput,
} from '@/types';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const LIMITS = { name: 80, location: 80, languages: 10, destinations: 12 } as const;

interface CardForm {
  name: string;
  location: string;
  languages: string[];
  destinations: string[];
  photo: { sourceUrl: string; sourcePublicId?: string; crop?: IdentityCardCrop } | null;
}

function fromContent(content: IdentityCardContent): CardForm {
  return {
    name: content.name,
    location: content.location ?? '',
    languages: [...content.languages],
    destinations: [...content.destinations],
    photo: content.photo
      ? { sourceUrl: content.photo.sourceUrl, sourcePublicId: content.photo.sourcePublicId, crop: content.photo.crop }
      : null,
  };
}

function toInput(form: CardForm): IdentityCardDraftInput {
  return {
    name: form.name.trim(),
    location: form.location.trim() || undefined,
    languages: form.languages.map((v) => v.trim()).filter(Boolean),
    destinations: form.destinations.map((v) => v.trim()).filter(Boolean),
    photo: form.photo
      ? {
          sourceUrl: form.photo.sourceUrl,
          ...(form.photo.sourcePublicId ? { sourcePublicId: form.photo.sourcePublicId } : {}),
          ...(form.photo.crop ? { crop: form.photo.crop } : {}),
        }
      : null,
  };
}

const sameForm = (a: CardForm, b: CardForm) => JSON.stringify(toInput(a)) === JSON.stringify(toInput(b));

function errorInfo(error: unknown): { conflict: boolean; message: string } {
  const axiosError = error as AxiosError<{ message?: string }>;
  return {
    conflict: axiosError?.response?.status === 409,
    message: axiosError?.response?.data?.message ?? 'Something went wrong. Please try again.',
  };
}

const STATE_COPY: Record<AdminIdentityCard['state'], { severity: 'warning' | 'info' | 'success'; text: string }> = {
  unpublished: {
    severity: 'warning',
    text: 'Not published yet — the public link shows "not found" and the guide cannot download the card until you publish.',
  },
  changes_pending: {
    severity: 'info',
    text: 'The live card differs from the saved draft. Travelers keep seeing the published version until you publish again.',
  },
  published: { severity: 'success', text: 'Live. The public link shows this card.' },
  legacy: {
    severity: 'success',
    text: 'Live (issued before card review). Your first publish freezes the card so later profile edits no longer change it.',
  },
};

function ListField({
  label,
  value,
  onChange,
  options,
  max,
  helper,
}: {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: string[];
  max: number;
  helper: string;
}) {
  return (
    <Autocomplete
      multiple
      freeSolo
      options={options.filter((o) => !value.includes(o))}
      value={value}
      onChange={(_, next) => onChange((next as string[]).map((v) => v.trim()).filter(Boolean).slice(0, max))}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const { key, ...props } = getTagProps({ index });
          return <Chip key={key} label={tag} size="small" color={index < 3 ? 'primary' : 'default'} {...props} />;
        })
      }
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={`${helper} Press Enter to add. Up to ${max}.`} />
      )}
    />
  );
}

export function AdminIdentityCardEditorPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryKey = [...adminKeys.review(id), 'identity-card'] as const;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => adminApi.getIdentityCard(id),
    enabled: Boolean(id),
    // Server data replaces the form, so only reload when the admin asks (or after a save).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [form, setForm] = useState<CardForm | null>(null);
  const [baseline, setBaseline] = useState<CardForm | null>(null);
  const [tab, setTab] = useState<'draft' | 'published'>('draft');
  const [cropOpen, setCropOpen] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Load (and reload after refetch) the editor from the server draft.
  useEffect(() => {
    if (!data) return;
    const loaded = fromContent(data.draft);
    setForm(loaded);
    setBaseline(loaded);
  }, [data]);

  const dirty = Boolean(form && baseline && !sameForm(form, baseline));

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const applyServerCard = (card: AdminIdentityCard) => {
    queryClient.setQueryData(queryKey, card);
    queryClient.invalidateQueries({ queryKey: adminKeys.review(id), exact: true });
    queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'guides'] });
  };

  const handleError = (err: unknown) => {
    const info = errorInfo(err);
    if (info.conflict) setConflict(true);
    else setToast({ message: info.message, severity: 'error' });
  };

  const saveMutation = useMutation({
    mutationFn: (input: IdentityCardDraftInput) => adminApi.saveIdentityCardDraft(id, input, data?.draftUpdatedAt ?? null),
    onSuccess: (card) => {
      applyServerCard(card);
      setToast({ message: 'Draft saved. The public card is unchanged until you publish.', severity: 'success' });
    },
    onError: handleError,
  });

  const discardMutation = useMutation({
    mutationFn: () => adminApi.discardIdentityCardDraft(id, data?.draftUpdatedAt ?? null),
    onSuccess: (card) => {
      applyServerCard(card);
      setToast({ message: 'Draft discarded.', severity: 'success' });
    },
    onError: handleError,
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      // Publish exactly what's on screen: save unsaved edits first.
      let token = data?.draftUpdatedAt ?? null;
      if (dirty && form) {
        const saved = await adminApi.saveIdentityCardDraft(id, toInput(form), token);
        // Keep the new concurrency token even if the publish below fails.
        applyServerCard(saved);
        token = saved.draftUpdatedAt;
      }
      return adminApi.publishIdentityCard(id, token);
    },
    onSuccess: (card) => {
      applyServerCard(card);
      setConfirmPublish(false);
      setTab('published');
      setToast({ message: 'Identity card published. The public link is live.', severity: 'success' });
    },
    onError: (err) => {
      setConfirmPublish(false);
      handleError(err);
    },
  });

  const busy = saveMutation.isPending || discardMutation.isPending || publishMutation.isPending || uploading;

  const update = (patch: Partial<CardForm>) => setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const previewPhotoUrl = form?.photo ? previewCardPhotoUrl(form.photo.sourceUrl, form.photo.crop) : undefined;
  const draftCard = useMemo(
    () =>
      data && form
        ? toGuideCardData(data.identity, {
            name: form.name.trim() || 'Guide name',
            photo: previewPhotoUrl,
            location: form.location,
            languages: form.languages,
            destinations: form.destinations,
          }, id)
        : null,
    [data, form, previewPhotoUrl, id]
  );
  const publishedCard = useMemo(
    () =>
      data?.published && data.state !== 'unpublished'
        ? toGuideCardData(data.identity, { ...data.published, photo: data.published.photo?.url }, id)
        : null,
    [data, id]
  );

  const nameError = form && !form.name.trim() ? 'Name is required' : undefined;
  const canSave = Boolean(form) && !nameError && !busy;
  // A published card with no draft and no edits has nothing new to publish.
  const hasChangesToPublish = dirty || data?.state !== 'published';

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setToast({ message: 'Use a JPG, PNG or WebP image.', severity: 'error' });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setToast({ message: 'Image must be 10 MB or smaller.', severity: 'error' });
      return;
    }
    setUploading(true);
    try {
      const uploaded = await adminApi.uploadIdentityCardPhoto(id, file);
      update({ photo: { sourceUrl: uploaded.url, sourcePublicId: uploaded.publicId } });
      setCropOpen(true);
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const reloadAfterConflict = async () => {
    setConflict(false);
    await refetch();
  };

  if (isLoading || (!form && !isError)) {
    return (
      <Box>
        <Skeleton variant="text" width={320} height={48} />
        <Skeleton variant="rounded" sx={{ width: '100%', maxWidth: 760, aspectRatio: '85.6 / 54', height: 'auto' }} />
      </Box>
    );
  }

  if (isError || !data || !form) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ADMIN_ROUTES.review(id))} sx={{ mb: 2 }}>
          Back to guide
        </Button>
        <Alert severity="error">{errorInfo(error).message}</Alert>
      </Box>
    );
  }

  const status = STATE_COPY[data.state];
  const photoCroppable = Boolean(form.photo && isCroppableImage(form.photo.sourceUrl));
  const usingProfilePhoto = Boolean(data.profilePhoto && form.photo?.sourceUrl === data.profilePhoto.url);
  const hasDraftToDiscard = dirty || data.hasSavedDraft;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ADMIN_ROUTES.review(id))} sx={{ mb: 1 }}>
        Back to guide
      </Button>
      <AdminPageHeader
        title="Identity card"
        subtitle={`${data.identity.guideId} · Prepare the card, then publish to make the public link live.`}
        actions={
          data.state !== 'unpublished' ? (
            <Button href={data.identityUrl} target="_blank" rel="noopener" startIcon={<OpenInNewIcon />}>
              View public link
            </Button>
          ) : undefined
        }
      />

      <Alert severity={status.severity} sx={{ mb: 2 }}>
        {status.text}
        {data.publishedAt && data.state !== 'unpublished'
          ? ` Last published ${new Date(data.publishedAt).toLocaleString()}.`
          : ''}
      </Alert>
      {!data.canPublish ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Publishing needs an active identity and an approved guide. You can still prepare and save the draft.
        </Alert>
      ) : null}
      {conflict ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reloadAfterConflict}>
              Reload
            </Button>
          }
        >
          Another admin updated this card. Reload to see the latest version (your unsaved edits will be replaced).
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.6fr) minmax(340px, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* Preview */}
        <Box sx={{ minWidth: 0 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} aria-label="Card version">
            <Tab
              value="draft"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Draft
                  {dirty ? <Chip size="small" color="warning" label="Unsaved" /> : null}
                </Box>
              }
            />
            <Tab value="published" label="Published" disabled={!publishedCard} />
          </Tabs>

          {tab === 'draft' && draftCard ? <GuideIdentityCard card={draftCard} /> : null}
          {tab === 'published' && publishedCard ? (
            <>
              <GuideIdentityCard card={publishedCard} />
              <Box sx={{ maxWidth: 760, mx: 'auto', mt: 3 }}>
                <GuideCardActions card={publishedCard} />
              </Box>
            </>
          ) : null}
        </Box>

        {/* Editor */}
        <Paper elevation={0} sx={{ ...dashboardSectionPanelSx, p: 3 }}>
          <Typography variant="subtitle1" component="h2" fontWeight={800} sx={{ mb: 2 }}>
            Card details
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Photo
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 84,
                aspectRatio: '210 / 340',
                borderRadius: 1.5,
                overflow: 'hidden',
                bgcolor: 'grey.200',
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {previewPhotoUrl ? (
                <Box component="img" src={previewPhotoUrl} alt="Card photo preview" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, textAlign: 'center' }}>
                  No photo
                </Typography>
              )}
            </Box>
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CropIcon />}
                disabled={!photoCroppable || busy}
                onClick={() => setCropOpen(true)}
              >
                Crop &amp; adjust
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                disabled={busy}
                onClick={() => fileInput.current?.click()}
              >
                Replace photo
              </Button>
              {data.profilePhoto && !usingProfilePhoto ? (
                <Button
                  size="small"
                  startIcon={<PersonOutlineIcon />}
                  disabled={busy}
                  onClick={() => update({ photo: { sourceUrl: data.profilePhoto!.url, sourcePublicId: data.profilePhoto!.publicId } })}
                >
                  Use profile photo
                </Button>
              ) : null}
              {form.photo ? (
                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={() => update({ photo: null })}>
                  Remove photo
                </Button>
              ) : null}
            </Stack>
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              aria-label="Upload replacement photo"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
          </Box>
          {form.photo && !photoCroppable ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              This photo is not stored on our image service, so it can't be cropped. Upload a replacement to crop it.
            </Typography>
          ) : null}

          <Divider sx={{ my: 2.5 }} />

          <Stack spacing={2.5}>
            <TextField
              label="Name on card"
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              error={Boolean(nameError)}
              helperText={nameError ?? 'Exactly as it should appear, e.g. "Aloke Mukherjee".'}
              inputProps={{ maxLength: LIMITS.name }}
              required
              fullWidth
            />
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => update({ location: e.target.value })}
              helperText='Shown under the name, e.g. "Mathura, Uttar Pradesh".'
              inputProps={{ maxLength: LIMITS.location }}
              fullWidth
            />
            <ListField
              label="Languages"
              value={form.languages}
              onChange={(languages) => update({ languages })}
              options={data.seed.languages}
              max={LIMITS.languages}
              helper="The first 3 appear on the card."
            />
            <ListField
              label="Destinations"
              value={form.destinations}
              onChange={(destinations) => update({ destinations })}
              options={data.seed.destinations}
              max={LIMITS.destinations}
              helper="The first 3 appear on the card."
            />
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              disabled={busy}
              onClick={() => update(fromContent(data.seed))}
              sx={{ alignSelf: 'flex-start' }}
            >
              Reset all fields to the guide's profile
            </Button>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Stack spacing={1.25}>
            <Button
              variant="contained"
              startIcon={publishMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
              disabled={!canSave || !data.canPublish || !hasChangesToPublish}
              onClick={() => setConfirmPublish(true)}
            >
              {data.state === 'unpublished' ? 'Publish card' : 'Publish changes'}
            </Button>
            <Button
              variant="outlined"
              startIcon={saveMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
              disabled={!canSave || !dirty}
              onClick={() => saveMutation.mutate(toInput(form))}
            >
              Save draft
            </Button>
            <Button
              color="inherit"
              disabled={!hasDraftToDiscard || busy}
              onClick={() => (data.hasSavedDraft ? discardMutation.mutate() : setForm(baseline))}
            >
              Discard changes
            </Button>
          </Stack>
        </Paper>
      </Box>

      {form.photo ? (
        <IdentityPhotoCropDialog
          open={cropOpen}
          imageUrl={form.photo.sourceUrl}
          initialCrop={form.photo.crop}
          onCancel={() => setCropOpen(false)}
          onApply={(crop) => {
            update({ photo: { ...form.photo!, crop } });
            setCropOpen(false);
          }}
        />
      ) : null}

      <Dialog open={confirmPublish} onClose={() => !publishMutation.isPending && setConfirmPublish(false)}>
        <DialogTitle>Publish identity card?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The card shown in the Draft tab goes live at <strong>{data.identityUrl.replace(/^https?:\/\//, '')}</strong>.
            The guide is notified and can download, print and share it from their dashboard.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublish(false)} disabled={publishMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
            {publishMutation.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
        <DialogTitle>Leave without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>Your unsaved card edits will be lost.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()}>Stay</Button>
          <Button color="error" onClick={() => blocker.proceed?.()}>
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            role={toast.severity === 'error' ? 'alert' : 'status'}
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
